/**
 * Typed client for GET /admin-services/k8s
 *
 * The controller wraps via utils.handleSuccess → { success: true, data: <payload> }.
 * client.get() returns { data: { success, data } }, so unwrap() peels both
 * layers to land on the raw payload.
 *
 * A 404 means the endpoint is NOT_CONFIGURED / not yet wired to the
 * DigitalOcean K8s API on the server — the UI should render the
 * "not connected" empty state rather than an error.
 */
import { AxiosError } from 'axios';
import { client, unwrap } from './client';

// ─── Payload shapes (as documented in the source JSDoc + consumed by
//     the legacy K8sHealth panel) ───────────────────────────────────────────

export interface K8sCluster {
  name?: string;
  region?: string;
  version?: string;
  nodeCount?: number;
  nodeReady?: number;
}

export interface K8sDeployment {
  name: string;
  replicas: number;
  available: number;
  restarts?: number;
  image?: string;
}

export interface K8sPod {
  name: string;
  namespace: string;
  status: string;
  restarts?: number;
  age?: string;
  cpuPct?: number;
  memMb?: number;
  node?: string;
}

export interface K8sEvent {
  type: string; // 'Warning' | 'Normal'
  reason: string;
  object: string;
  message: string;
  age: string;
}

export interface K8sHpa {
  deployment: string;
  min: number;
  max: number;
  current: number;
  targetCpu?: number;
  currentCpu?: number;
}

export interface K8sHealthPayload {
  cluster: K8sCluster;
  deployments: K8sDeployment[];
  pods: K8sPod[];
  events: K8sEvent[];
  hpa: K8sHpa[];
}

// ─── Fetch ────────────────────────────────────────────────────────────────

/**
 * Returns null on 404 (endpoint not yet connected to DO K8s API).
 * All other errors are re-thrown for react-query to surface.
 */
export async function fetchK8sHealth(): Promise<K8sHealthPayload | null> {
  try {
    return await unwrap<K8sHealthPayload>(
      client.get<{ data: K8sHealthPayload }>('/admin-services/k8s')
    );
  } catch (err) {
    const axErr = err as AxiosError;
    if (axErr?.response?.status === 404) {
      return null;
    }
    throw err;
  }
}
